/**
 * useSchemaVersionErrorToast Hook
 *
 * Subscribes to BackgroundSyncService schema version errors and shows
 * toast notifications when errors occur.
 *
 * **Handles all schema version error codes**:
 * - `SCHEMA_VERSION_NEWER`: Stale browser - shows toast with Refresh button
 * - `SCHEMA_VERSION_MISSING/INVALID/MISMATCH`: Corrupted data - shows toast with Settings link
 *
 * **Dedup Logic**:
 * - Uses ref to track previous error code
 * - On subsequent callbacks: only toast if error code changed
 *
 * **Initial-mount behavior** (differentiated by error type):
 * - `SCHEMA_VERSION_NEWER`: Skip toast on initial mount if error already exists
 *   (user will eventually refresh, and problem self-resolves)
 * - `SCHEMA_VERSION_MISSING/INVALID/MISMATCH`: Show toast even on initial mount
 *   (corrupted data requires manual action - one reminder per page load is appropriate)
 *
 * @module useSchemaVersionErrorToast
 */

import { useEffect, useRef } from 'react';
import { BackgroundSyncService } from '@/services/sync/BackgroundSyncService';
import type { SchemaVersionError, SchemaVersionErrorCode } from '@/types/sync/SyncProvider';
import { buildSchemaVersionErrorToastShape } from '@/utils';
import { toastShapeToOptions } from '@/components/shared/Toast';
import { addToast } from '@enfomentis/ui';

/**
 * Hook that subscribes to schema version errors and shows toast notifications.
 *
 * Should be called once in a global provider (e.g., PrivateAppProvider) to ensure
 * toasts are shown regardless of which page the user is on.
 *
 * @example
 * ```tsx
 * // In PrivateAppProvider.tsx
 * function PrivateAppProvider({ children }) {
 *   useSchemaVersionErrorToast();
 *   return <>{children}</>;
 * }
 * ```
 */
export function useSchemaVersionErrorToast(): void {
  // Track previous error code to prevent duplicate toasts
  const prevErrorCodeRef = useRef<SchemaVersionErrorCode | null>(null);

  // Track if this is the first callback (initial mount)
  const isFirstCallbackRef = useRef(true);

  useEffect(() => {
    const syncService = BackgroundSyncService.getInstance();

    /**
     * Handle schema version error changes.
     *
     * @param error - Schema version error or null if cleared
     */
    const handleSchemaVersionError = (error: SchemaVersionError | null): void => {
      // Error cleared - reset tracking refs
      if (error === null) {
        prevErrorCodeRef.current = null;
        isFirstCallbackRef.current = false;
        return;
      }

      // Initial mount guard: differentiated by error type
      if (isFirstCallbackRef.current) {
        isFirstCallbackRef.current = false;

        // For SCHEMA_VERSION_NEWER: skip toast on initial mount
        // User will eventually refresh, problem self-resolves
        if (error.code === 'SCHEMA_VERSION_NEWER') {
          prevErrorCodeRef.current = error.code;
          console.log('[useSchemaVersionErrorToast] Initial mount - NEWER error, skipping toast');
          return;
        }

        // For corrupted data errors (MISSING/INVALID/MISMATCH): show toast even on initial mount
        // These require manual action and won't self-resolve - one reminder per page load
        console.log('[useSchemaVersionErrorToast] Initial mount - corrupted data error, showing toast');
        // Fall through to show toast
      }

      // Check if error code changed (dedup)
      if (error.code === prevErrorCodeRef.current) {
        // Same error code - don't show duplicate toast
        return;
      }

      // New error - update ref and show toast
      prevErrorCodeRef.current = error.code;

      console.log('[useSchemaVersionErrorToast] Schema version error detected:', {
        code: error.code,
        localVersion: error.localVersion,
        remoteVersion: error.remoteVersion,
      });

      // Build and show toast
      const toastShape = buildSchemaVersionErrorToastShape(error);
      const toastOptions = toastShapeToOptions(toastShape);
      addToast(toastOptions);
    };

    // Subscribe to schema version errors
    const unsubscribe = syncService.onSchemaVersionError(handleSchemaVersionError);

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, []);
}
